import React, { useId, useState } from 'react';

import {
    IconArrowDown,
    IconArrowRight,
    IconArrowUp
} from '$partials/icon/index.js';

import { formatLinkBlockToUrl } from '$utilities/format.js';
import { isLocalURL } from '$utilities/browser';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/modules.d.ts').AccordionLarge}
 */
const AccordionLarge = ({
    label,
    modulePaddingClasses = '',
    rows
}) => {
    const panelIdPrefix = useId();

    const [openRows, setOpenRows] = useState(() => (rows?.length ? [rows[0].id] : []));

    const toggleRow = (id) => {
        setOpenRows((open) => {
            if (open.includes(id)) {
                return open.filter((openId) => openId !== id);
            }

            return [...open, id];
        });
    };

    return (
        <section className='module-accordion-large'>
            <div className={`layout-container bg-neutral-00 ${modulePaddingClasses}`}>
                <div className='layout-grid flex-col sm:flex-row gap-y-10 sm:gap-y-0'>
                    <div className='sm:w-4/12 md:w-3/12 layout-grid-col'>
                        {label && (
                            <h2 className='type-style-heading'>{label}</h2>
                        )}
                    </div>

                    <div className='flex flex-col gap-y-16 sm:gap-y-32 sm:w-8/12 md:w-9/12'>
                        {rows?.length > 0 && (
                            <div className='w-full layout-grid-col'>
                                {rows.map((row) => {
                                    const isOpen = openRows.includes(row.id);

                                    return (
                                        <div
                                            className='border-t border-neutral-400'
                                            key={row.id}>
                                            <h3>
                                                <button
                                                    aria-controls={`${panelIdPrefix}-${row.id}`}
                                                    aria-expanded={isOpen}
                                                    className='w-full flex items-start justify-between gap-x-8 py-16 text-left cursor-pointer type-style-heading'
                                                    onClick={() => toggleRow(row.id)}
                                                    type='button'>
                                                    {row.heading}

                                                    <span className='shrink-0 mt-4'>
                                                        <IconArrowDown
                                                            className={`transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
                                                            height={20}
                                                            width={20} />
                                                    </span>
                                                </button>
                                            </h3>

                                            <div
                                                hidden={!isOpen}
                                                id={`${panelIdPrefix}-${row.id}`}>
                                                {row.body?.length > 0 && (
                                                    <div className='flex flex-col gap-y-16 sm:gap-y-24 pb-32'>
                                                        {row.body.map((item) => {
                                                            const href = formatLinkBlockToUrl(item.ctaLink);
                                                            const isExternal = href && !isLocalURL(href);

                                                            return (
                                                                <div
                                                                    className='layout-grid flex-col sm:flex-row gap-y-5 sm:gap-y-0'
                                                                    key={item.id}>
                                                                    {item.subHeading && (
                                                                        <h4 className='w-full sm:w-2/8 layout-grid-col type-style-body-bold'>{item.subHeading}</h4>
                                                                    )}

                                                                    <div className='w-full sm:w-4/8 layout-grid-col'>
                                                                        {item.text && (
                                                                            <div
                                                                                className='rich-text type-style-body'
                                                                                dangerouslySetInnerHTML={{ __html: item.text }} />
                                                                        )}

                                                                        {(item.ctaLabel && href) && (
                                                                            <p className='mt-32'>
                                                                                <a
                                                                                    className='button type-style-body cursor-pointer'
                                                                                    href={href}
                                                                                    rel={isExternal ? 'noopener noreferrer' : undefined}
                                                                                    target={isExternal ? '_blank' : undefined}>
                                                                                    {item.ctaLabel}

                                                                                    <span
                                                                                        aria-hidden={true}
                                                                                        className='ml-5'>
                                                                                        <IconArrowRight
                                                                                            height={20}
                                                                                            width={20} />
                                                                                    </span>
                                                                                </a>
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default AccordionLarge;
